@if (visible()) {
  @if (multi()) {
    <!-- Multi-selection: accordion of per-cell note lists.
         Custom accordion (not <details>) so we can animate the panel smoothly
         via the grid-template-rows 0fr→1fr trick and own the aria semantics. -->
    <div class="notes-strip mode-rows" role="region" [attr.aria-label]="'notes.strip.aria' | t"
         #panel
         (pointerenter)="onNotesEnter()" (pointerleave)="onNotesLeave()">
      <div class="notes-strip-header">
        <span class="notes-strip-title">{{ 'notes.strip.title' | t }}</span>
        <button type="button" class="notes-strip-hide" (click)="hide()"
                [attr.aria-label]="'notes.hide' | t" [title]="'notes.hide' | t">
          {{ 'notes.hide' | t }}
        </button>
      </div>
      @if (hasQuestions()) {
        <div class="notes-strip-filter" role="group" aria-label="filter by kind">
          <button type="button" class="kind-tab"
                  [class.is-active]="kindFilter() === 'all'"
                  (click)="setKindFilter('all')">All</button>
          <button type="button" class="kind-tab"
                  [class.is-active]="kindFilter() === 'q'"
                  (click)="setKindFilter('q')">
            <span class="kind-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5"/><circle cx="12" cy="17" r="0.7" fill="currentColor" stroke="none"/></svg>
            </span>
            Questions
          </button>
          <button type="button" class="kind-tab"
                  [class.is-active]="kindFilter() === 'note'"
                  (click)="setKindFilter('note')">
            <span class="kind-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false"><path d="M5 4h11l4 4v12H5z"/><path d="M16 4v4h4"/><path d="M8 12h8M8 16h6"/></svg>
            </span>
            Notes
          </button>
        </div>
      }
      <div class="notes-strip-body notes-strip-accordion">
        @for (group of groups(); track trackByGroup($index, group)) {
          <div class="accordion-group" [class.is-open]="group.expanded">
            <button
              type="button"
              class="accordion-head"
              [attr.aria-expanded]="group.expanded"
              [attr.aria-controls]="'notes-panel-' + group.cell"
              (click)="toggleGroup(group.cell)"
            >
              <span class="accordion-chevron" aria-hidden="true">
                <svg viewBox="0 0 12 12" width="10" height="10" focusable="false">
                  <path d="M3 4.5 L6 7.5 L9 4.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <span class="accordion-cell">{{ group.cell }}</span>
              <span class="accordion-count" aria-hidden="true">{{ group.notes.length }}</span>
              <span class="visually-hidden">{{ group.notes.length }} notes</span>
            </button>
            <div class="accordion-panel" [id]="'notes-panel-' + group.cell" role="region" [attr.aria-hidden]="!group.expanded">
              <div class="accordion-panel-inner">
                <ul class="strip-list strip-list-nested" role="list">
                  @for (note of group.notes; track trackById($index, note)) {
                    <li class="note-row" role="listitem"
                        [class.note-row-q]="noteKind(note) === 'q'"
                        [class.note-row-a]="noteKind(note) === 'a'">
                      <button class="note-open" type="button" (click)="openInGroup(group.cell, note.id)" [title]="note.text">
                        @if (noteKind(note) !== 'note') {
                          <span class="row-kind" [attr.data-kind]="noteKind(note)">
                            <span class="kind-icon" aria-hidden="true">
                              @switch (noteKind(note)) {
                                @case ('q')  { <svg viewBox="0 0 24 24" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5"/><circle cx="12" cy="17" r="0.7" fill="currentColor" stroke="none"/></svg> }
                                @case ('a')  { <svg viewBox="0 0 24 24" focusable="false"><path d="M21 11.5a8.4 8.4 0 0 1-8.4 8.4c-1.7 0-3.3-.5-4.6-1.4L3 20l1.4-4.4A8.4 8.4 0 1 1 21 11.5z"/></svg> }
                              }
                            </span>
                            <span class="kind-label">{{ noteKindLabel(note) }}</span>
                          </span>
                        }
                        <span class="row-text">{{ noteDisplayText(note) }}</span>
                      </button>
                      <button class="note-delete" type="button"
                              (click)="removeInGroup(group.cell, note.id, $event)"
                              [attr.aria-label]="'notes.delete' | t" [title]="'notes.delete' | t">×</button>
                    </li>
                  }
                  <li class="add-rule-row" role="presentation">
                    <button type="button" class="add-rule-btn" (click)="captureForEmpty(group.cell)">
                      {{ 'notes.addNote' | t }}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        }
        @if (emptyCells().length > 0) {
          <!-- Empty cells: selected tiles that don't have notes yet. Surfaced
               as a flat list at the bottom so the user can see what's still
               missing context. Clicking a pill drops the cursor into the
               command line in capture mode for that cell. -->
          <div class="accordion-empty">
            <div class="accordion-empty-label">No notes</div>
            <ul class="accordion-empty-list" role="list">
              @for (cell of emptyCells(); track cell) {
                <li class="accordion-empty-item-wrap" role="listitem">
                  <button
                    type="button"
                    class="accordion-empty-item"
                    (click)="captureForEmpty(cell)"
                    [title]="'Add a note to ' + cell"
                  >{{ cell }}</button>
                </li>
              }
            </ul>
          </div>
        }
      </div>
    </div>
  } @else {
    <!-- Single-selection: existing flat list / chip layout -->
    <div class="notes-strip" [class.mode-chips]="mode() === 'chips'" [class.mode-rows]="mode() === 'rows'"
         #panel
         role="list" [attr.aria-label]="'notes.strip.aria' | t"
         (pointerenter)="onNotesEnter()" (pointerleave)="onNotesLeave()">
      <div class="notes-strip-header">
        <span class="notes-strip-title">{{ 'notes.strip.title' | t }}</span>
        <button type="button" class="notes-strip-hide" (click)="hide()"
                [attr.aria-label]="'notes.hide' | t" [title]="'notes.hide' | t">
          {{ 'notes.hide' | t }}
        </button>
      </div>
      @if (hasQuestions()) {
        <div class="notes-strip-filter" role="group" aria-label="filter by kind">
          <button type="button" class="kind-tab"
                  [class.is-active]="kindFilter() === 'all'"
                  (click)="setKindFilter('all')">All</button>
          <button type="button" class="kind-tab"
                  [class.is-active]="kindFilter() === 'q'"
                  (click)="setKindFilter('q')">
            <span class="kind-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5"/><circle cx="12" cy="17" r="0.7" fill="currentColor" stroke="none"/></svg>
            </span>
            Questions
          </button>
          <button type="button" class="kind-tab"
                  [class.is-active]="kindFilter() === 'note'"
                  (click)="setKindFilter('note')">
            <span class="kind-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false"><path d="M5 4h11l4 4v12H5z"/><path d="M16 4v4h4"/><path d="M8 12h8M8 16h6"/></svg>
            </span>
            Notes
          </button>
        </div>
      }
      <div class="notes-strip-body">
        @if (mode() === 'chips') {
          <div class="chip-track">
            @for (note of notes(); track trackById($index, note)) {
              <div class="chip-wrap" role="listitem"
                   [class.note-row-q]="noteKind(note) === 'q'"
                   [class.note-row-a]="noteKind(note) === 'a'">
                <button
                  class="note-chip"
                  type="button"
                  (click)="open(note.id)"
                  [title]="note.text"
                >
                  @if (noteKind(note) !== 'note') {
                    <span class="row-kind chip-kind" [attr.data-kind]="noteKind(note)">
                      <span class="kind-icon" aria-hidden="true">
                        @switch (noteKind(note)) {
                          @case ('q')  { <svg viewBox="0 0 24 24" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5"/><circle cx="12" cy="17" r="0.7" fill="currentColor" stroke="none"/></svg> }
                          @case ('a')  { <svg viewBox="0 0 24 24" focusable="false"><path d="M21 11.5a8.4 8.4 0 0 1-8.4 8.4c-1.7 0-3.3-.5-4.6-1.4L3 20l1.4-4.4A8.4 8.4 0 1 1 21 11.5z"/></svg> }
                        }
                      </span>
                    </span>
                  }
                  <span class="chip-text">{{ preview(noteDisplayText(note)) }}</span>
                </button>
                <button
                  class="note-delete"
                  type="button"
                  (click)="remove(note.id, $event)"
                  [attr.aria-label]="'notes.delete' | t"
                  [title]="'notes.delete' | t"
                >×</button>
              </div>
            }
          </div>
        } @else {
          <ul class="strip-list">
            @for (note of notes(); track trackById($index, note)) {
              <li class="note-row" role="listitem"
                  [class.note-row-q]="noteKind(note) === 'q'"
                  [class.note-row-a]="noteKind(note) === 'a'">
                <button
                  class="note-open"
                  type="button"
                  (click)="open(note.id)"
                  [title]="note.text"
                >
                  <span class="row-text">{{ noteDisplayText(note) }}</span>
                </button>
                <button
                  class="note-delete"
                  type="button"
                  (click)="remove(note.id, $event)"
                  [attr.aria-label]="'notes.delete' | t"
                  [title]="'notes.delete' | t"
                >×</button>
              </li>
            }
            <li class="add-rule-row" role="presentation">
              <button type="button" class="add-rule-btn" (click)="add()">
                {{ 'notes.addNote' | t }}
              </button>
            </li>
          </ul>
        }
      </div>
    </div>
  }
}
